import { useState } from "react";
import {
  Flex,
  Text,
  Menu,
  UnstyledButton,
  useMantineTheme,
  Skeleton,
} from "@mantine/core";
import { Calendar, ArrowDown2 } from "iconsax-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  IListPredictive,
  useInfinitePredictives,
} from "../../hooks/usePredictives";
import { whitelabel_id } from "../../api";
import { meetingDateFormatted } from "../../utils/date";
import { generateSlug } from "../../utils/cleanSlug";
import { useTranslation } from "react-i18next";

interface PredictiveDateSelectorProps {
  currentData?: {
    id: number;
    title: string;
    expiration: string;
    category: string;
  };
  isLoading?: boolean;
}

export default function PredictiveDateSelector({
  currentData,
  isLoading = false,
}: PredictiveDateSelectorProps) {
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [opened, setOpened] = useState(false);
  const currentId = slug?.split("&id=")[1];

  const { data: predictivesData, isLoading: isPredictivesLoading } =
    useInfinitePredictives({
      page: 1,
      category: "",
      white_label_id: whitelabel_id,
    });

  const allPredictives =
    predictivesData?.pages.flatMap((page) => page.data) || [];

  const handleSelectDate = (predictive: IListPredictive) => {
    navigate(
      `/marketplace/predix/detalhes/${generateSlug(
        predictive.title
      )}&id=${predictive.id.toString()}`,
      {
        preventScrollReset: false,
      }
    );
    setOpened(false);
  };

  const currentDateFormatted = currentData
    ? meetingDateFormatted({
        date: currentData.expiration,
        category: currentData.category,
      })
    : null;

  if (isLoading || isPredictivesLoading) {
    return (
      <Flex
        gap="0.5rem"
        align="center"
        bg={theme.colors.primaryOpacity[0]}
        p="0.75rem"
        style={{ borderRadius: "0.75rem" }}
      >
        <Calendar size="32" color="#1E3A5F" />
        <Skeleton height={40} width={180} />
      </Flex>
    );
  }

  if (allPredictives.length <= 1) {
    return (
      <Flex
        gap="0.5rem"
        align="center"
        bg={theme.colors.primaryOpacity[0]}
        p="0.75rem"
        style={{ borderRadius: "0.75rem" }}
      >
        <Calendar size="32" color="#1E3A5F" />
        {currentDateFormatted && (
          <Flex direction="column" gap="0.125rem">
            <Text
              fz="1rem"
              fw={500}
              color="basic.5"
              style={{ whiteSpace: "nowrap" }}
            >
              {currentDateFormatted.title}
            </Text>
            <Text
              fz="1rem"
              fw={600}
              color="basic.5"
              style={{ whiteSpace: "nowrap" }}
            >
              {currentDateFormatted.dateRange}
            </Text>
          </Flex>
        )}
      </Flex>
    );
  }

  return (
    <Menu
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      withArrow
      shadow="md"
      offset={8}
    >
      <Menu.Target>
        <UnstyledButton
          style={{
            background: theme.colors.primaryOpacity[0],
            borderRadius: "0.75rem",
            padding: "0.75rem",
            transition: "all 0.2s ease",
            cursor: "pointer",
          }}
          sx={{
            "&:hover": {
              background: theme.colors.primaryOpacity[1],
            },
          }}
        >
          <Flex gap="0.5rem" align="center" justify={"space-between"}>
            <Calendar size="32" color="#1E3A5F" />
            {currentDateFormatted && (
              <Flex direction="column" gap="0.125rem">
                <Text
                  fz="1rem"
                  fw={500}
                  color="basic.5"
                  style={{ whiteSpace: "nowrap" }}
                >
                  {currentDateFormatted.title}
                </Text>
                <Text
                  fz="1rem"
                  fw={600}
                  color="basic.5"
                  style={{ whiteSpace: "nowrap" }}
                >
                  {currentDateFormatted.dateRange}
                </Text>
              </Flex>
            )}
            <ArrowDown2
              size="20"
              color={theme.colors.basic[5]}
              style={{
                transform: opened ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            />
          </Flex>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown
        style={{
          minWidth: "17.5rem",
          maxHeight: "25rem",
          overflowY: "auto",
        }}
      >
        <Menu.Label>
          <Text fz="0.75rem" fw={600} color="basic.4" tt="uppercase">
            {t("marketplace.predix.detalhes.select.title")}
          </Text>
        </Menu.Label>

        {allPredictives.map((predictive) => {
          const dateFormatted = meetingDateFormatted({
            date: predictive.expiration,
            category: predictive.category,
          });
          const isSelected = predictive.id === Number(currentId);

          return (
            <Menu.Item
              key={predictive.id}
              onClick={() => handleSelectDate(predictive)}
              style={{
                background: isSelected
                  ? theme.colors.primaryOpacity[0]
                  : "transparent",
                borderLeft: isSelected
                  ? `3px solid ${theme.colors.primary[1]}`
                  : "3px solid transparent",
                padding: "0.75rem 1rem",
              }}
              sx={{
                "&:hover": {
                  background: theme.colors.primaryOpacity[0],
                },
              }}
            >
              <Flex direction="column" gap="0.25rem">
                <Text fz="0.875rem" fw={600} color="basic.5">
                  {dateFormatted.title}
                </Text>
                <Text fz="0.8125rem" fw={500} color="basic.4">
                  {dateFormatted.dateRange}
                </Text>
              </Flex>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}
